import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLang } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtUSD } from "@/lib/api";
import type { BlockKey, Yacht } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  mode: "online" | "contact";
  yacht: Yacht;
  date: string;
  block: BlockKey;
  guests: number;
  amount: number;
  onConflict: () => void;
}

export default function BookingDialog({ open, onOpenChange, mode, yacht, date, block, guests, amount, onConflict }: Props) {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { ...form, yacht_id: yacht.id, date, block, guests, mode, lang });
      if (mode === "online") nav(data.checkout_path);
      else setDone(true);
    } catch (err) {
      const d = errDetail(err);
      toast.error(t(`err.${d}`) === `err.${d}` ? t("err.generic") : t(`err.${d}`));
      if (d === "date_unavailable") { onConflict(); onOpenChange(false); }
    } finally {
      setLoading(false);
    }
  };

  const close = (o: boolean) => { onOpenChange(o); if (!o) setDone(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="booking-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="contact-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#00A8A0]" />
            <h3 className="mt-4 font-display text-2xl text-[#0B192C]">{t("form.okTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("form.okBody")}</p>
            <button onClick={() => close(false)} data-testid="contact-success-close-btn" className="mt-6 rounded-full bg-[#0B192C] px-6 py-2.5 text-sm font-semibold text-white">{t("form.close")}</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#0B192C]">{mode === "online" ? t("form.titleOnline") : t("form.titleContact")}</DialogTitle>
              <DialogDescription data-testid="booking-dialog-summary">
                {yacht.name} · {fmtDate(date, lang)} · {t(`block.${block}`)} · {guests} {t("card.guests")} · <b className="text-[#0B192C]">{fmtUSD(amount)}</b>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label htmlFor="bk-name">{t("form.name")}</Label><Input id="bk-name" required minLength={2} value={form.name} onChange={set("name")} data-testid="booking-name-input" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="bk-email">{t("form.email")}</Label><Input id="bk-email" type="email" required value={form.email} onChange={set("email")} data-testid="booking-email-input" /></div>
                <div className="space-y-1.5"><Label htmlFor="bk-phone">{t("form.phone")}</Label><Input id="bk-phone" required minLength={6} value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="bk-notes">{t("form.notes")}</Label><Textarea id="bk-notes" rows={3} value={form.notes} onChange={set("notes")} data-testid="booking-notes-input" /></div>
              <button type="submit" disabled={loading} data-testid="booking-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B192C] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#13294a] disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? t("form.sending") : mode === "online" ? t("form.submitOnline") : t("form.submitContact")}
              </button>
              <p className="text-center text-xs text-slate-500" data-testid="booking-terms-note">
                {t("book.accept").split(/\{terms\}|\{privacy\}/)[0]}
                <Link to="/terminos" target="_blank" className="underline hover:text-[#0B192C]">{t("legal.terms")}</Link>
                {t("book.accept").split("{terms}")[1].split("{privacy}")[0]}
                <Link to="/privacidad" target="_blank" className="underline hover:text-[#0B192C]">{t("legal.privacy")}</Link>
                {t("book.accept").split("{privacy}")[1]}
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
